<template>
    <el-dialog v-model="visible" title="定时开关机设置" width="1100px" append-to-body :close-on-click-modal="false">
        <div class="schedule-container" style="height: 65vh">
            <div class="mb-3" style="margin-bottom: 10px">
                <el-button type="primary" @click="handleAddGroup">+ 新增定时组别</el-button>
            </div>

            <!-- 定时组列表 -->
            <div class="group-item" v-for="group in groupList" :key="group.id">
                <div class="group-head">
                    <span class="title">{{ group.name }}</span>
                    <div class="actions">
                        <el-button text type="primary" @click="handleEditGroup(group)">编辑</el-button>
                        <el-button text type="danger" @click="handleDeleteGroup(group.id)">删除</el-button>
                    </div>
                </div>

                <div class="group-detail">
                    <div class="detail-item">
                        <label>执行周期：</label>
                        <span class="text">{{ weekText(group.week_time) }}</span>
                    </div>
                    <div class="detail-item">
                        <label>执行时段：</label>
                        <div class="time-list">
                            <span v-for="(t, i) in group.switch_time" :key="i" class="time-tag">
                                开机：{{ t.start_time || '未设置' }} → 关机：{{ t.end_time || '未设置' }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <Pagination
                :current-page="page_params.page"
                :page-size="page_params.page_size"
                :total="total"
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
            ></Pagination>
        </div>

        <!-- 编辑弹窗 -->
        <el-dialog v-model="formVisible" :title="editingGroup ? '编辑定时组别' : '新增定时组别'" width="800px" append-to-body>
            <el-form :model="form" label-width="110px" label-position="top">
                <el-form-item label="组别名称">
                    <el-input v-model="form.name" placeholder="如：定时开关机1组" />
                </el-form-item>

                <el-form-item label="执行周期">
                    <el-checkbox-group v-model="form.week_time">
                        <el-checkbox :label="1">周一</el-checkbox>
                        <el-checkbox :label="2">周二</el-checkbox>
                        <el-checkbox :label="3">周三</el-checkbox>
                        <el-checkbox :label="4">周四</el-checkbox>
                        <el-checkbox :label="5">周五</el-checkbox>
                        <el-checkbox :label="6">周六</el-checkbox>
                        <el-checkbox :label="0">周日</el-checkbox>
                    </el-checkbox-group>
                </el-form-item>

                <el-form-item label="开关机时间段">
                    <div
                        v-for="(t, i) in form.switch_time"
                        :key="i"
                        class="time-row"
                        style="display: flex; gap: 10px; margin-bottom: 8px"
                    >
                        <el-time-select v-model="t.start_time" placeholder="开机时间" start="00:00" step="00:05" end="23:59" />
                        <el-time-select v-model="t.end_time" placeholder="关机时间" start="00:00" step="00:05" end="23:59" />
                        <el-button text type="danger" @click="delTimeRow(i)">删除</el-button>
                    </div>
                    <el-button type="primary" plain size="small" @click="addTimeRow">+ 添加时间段</el-button>
                </el-form-item>

                <!-- TreeSelect：设备直接挂在单元下，去掉房间层级 -->
                <el-form-item label="选择设备">
                    <el-tree-select
                        ref="treeSelectRef"
                        v-model="treeSelectValue"
                        :data="deviceTreeData"
                        multiple
                        :max-collapse-tags="0"
                        :render-after-expand="false"
                        show-checkbox
                        check-strictly
                        node-key="id"
                        :props="treeProps"
                        placeholder="请选择设备"
                        style="width: 100%"
                        popper-class="device-tree-dropdown"
                        :cache-data="cacheData"
                        @check="handleTreeCheck"
                    >
                        <template #default="{ node, data }">
                            <span class="tree-node">
                                <el-icon v-if="data.isDevice" class="device-icon"><Monitor /></el-icon>
                                <el-icon v-else class="folder-icon"><Folder /></el-icon>
                                <span :class="{ 'device-name': data.isDevice }">{{ node.label }}</span>
                                <span v-if="data.isDevice && data.imei" class="imei-tag">{{ data.imei }}</span>
                                <span
                                    v-if="!data.isDevice && data.deviceCount !== undefined && data.deviceCount > 0"
                                    class="count-tag"
                                >
                                    ({{ data.deviceCount }})
                                </span>
                            </span>
                        </template>
                    </el-tree-select>
                    <div v-if="selectedDeviceCount > 0" class="selected-info">
                        已选择
                        <strong>{{ selectedDeviceCount }}</strong>
                        个设备
                    </div>
                </el-form-item>
            </el-form>

            <template #footer>
                <div class="text-right">
                    <el-button @click="formVisible = false">取消</el-button>
                    <el-button type="primary" @click="submitGroupForm">保存</el-button>
                </div>
            </template>
        </el-dialog>
    </el-dialog>
</template>

<script setup>
import { ref, reactive, onMounted, computed, nextTick, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Monitor, Folder } from '@element-plus/icons-vue'
import {
    getDeviceList,
    getGroupList,
    addGroupApi,
    putGroupApi,
    deleteGroupApi,
    getGroupDetailApi,
    getDeviceTree
} from '@/api/thermostat/devicePoint.js'
import Pagination from '@/components/Pagination/index.vue'

onMounted(() => {
    loadDevices()
})
const weekList = [
    { label: '周一', value: 1 },
    { label: '周二', value: 2 },
    { label: '周三', value: 3 },
    { label: '周四', value: 4 },
    { label: '周五', value: 5 },
    { label: '周六', value: 6 },
    { label: '周日', value: 0 }
]

const scheduleApi = {
    list: getGroupList,
    add: addGroupApi,
    update: putGroupApi,
    delete: deleteGroupApi,
    get: getGroupDetailApi
}

const visible = ref(false)
const groupList = ref([])
const deviceTreeData = ref([])
const treeSelectRef = ref(null)
const treeSelectValue = ref([])
const cacheData = ref([])

const formVisible = ref(false)
const editingGroup = ref(null)

const form = reactive({
    name: '',
    week_time: [],
    switch_time: [{ start_time: '08:00', end_time: '18:00' }],
    thermostat_list: []
})

const treeProps = {
    label: 'name',
    children: 'children',
    value: 'id',
    disabled: 'disabled'
}

const emit = defineEmits(['refresh'])
defineExpose({ open })
const deviceId = ref('')

const selectedDeviceCount = computed(() => {
    return form.thermostat_list?.length || 0
})

// 监听 TreeSelect 值变化，同步到 form.thermostat_list
watch(
    treeSelectValue,
    (newVal) => {
        if (!newVal || newVal.length === 0) {
            form.thermostat_list = []
            return
        }
        // treeSelectValue 中只包含设备id（父节点id已在 handleTreeCheck 中过滤）
        form.thermostat_list = [...newVal]
    },
    { deep: true }
)

async function open(data) {
    if (data) {
        deviceId.value = data.id
    }
    visible.value = true
    await loadDevices()
    await loadGroups()
}

// ==================== 核心：树形数据处理（去掉房间层级） ====================

/**
 * 构建树形数据：去掉房间层级，设备直接挂在单元(node_type=4)下
 *
 * 原始: 小区(1) → 单位(2) → 楼(3) → 单元(4) → 房间(room_type) → 设备
 * 现在: 小区(1) → 单位(2) → 楼(3) → 单元(4) → [设备1, 设备2, ...]
 *
 * 房间节点被拍平，设备直接作为单元的子节点
 */
function buildDeviceTree(nodes) {
    if (!nodes || !Array.isArray(nodes)) return []

    return nodes
        .map((node) => {
            const hasRoomType = !!node.room_type
            const hasDevices = node.device_list && node.device_list.length > 0
            const hasChildren = node.children && node.children.length > 0

            const treeNode = {
                id: node.id,
                name: node.name,
                level: node.level,
                node_type: node.node_type,
                original: node
            }

            // 如果是房间节点(room_type存在) → 不创建房间节点，直接返回其设备列表
            if (hasRoomType) {
                // 房间节点不显示，只返回设备（由父节点收集）
                return null
            }

            // 非房间节点，先递归处理子节点
            let childNodes = []
            if (hasChildren) {
                childNodes = buildDeviceTree(node.children)
                // 过滤掉 null（房间节点被过滤）
                childNodes = childNodes.filter((c) => c !== null)
            }

            // 如果当前节点是单元(node_type=4)，收集其下所有房间的设备
            if (node.node_type === 4) {
                const devices = collectDevicesFromUnit(node)
                if (devices.length > 0) {
                    // 设备直接挂在单元下
                    childNodes = [...childNodes, ...devices]
                }
            }

            if (childNodes.length > 0) {
                treeNode.children = childNodes
            }

            // 计算设备总数
            treeNode.deviceCount = countDevicesInTree(treeNode)

            return treeNode
        })
        .filter((node) => node !== null)
}

/**
 * 从单元节点下收集所有设备（遍历所有房间）
 * 房间被拍平，设备直接提取
 */
function collectDevicesFromUnit(unitNode) {
    const devices = []

    function traverse(node) {
        // 如果是房间且有设备，直接收集设备
        if (node.room_type && node.device_list) {
            for (const device of node.device_list) {
                devices.push({
                    id: device.id,
                    name: device.name,
                    imei: device.imei,
                    isDevice: true
                })
            }
        }
        // 继续递归子节点
        if (node.children && node.children.length > 0) {
            for (const child of node.children) {
                traverse(child)
            }
        }
    }

    if (unitNode.children) {
        for (const child of unitNode.children) {
            traverse(child)
        }
    }

    return devices
}

/**
 * 计算树节点下的设备总数
 */
function countDevicesInTree(treeNode) {
    let count = 0
    if (!treeNode.children) return 0

    for (const child of treeNode.children) {
        if (child.isDevice) {
            count += 1
        } else if (child.children) {
            count += countDevicesInTree(child)
        }
    }
    return count
}

/**
 * 扁平化收集设备节点（用于 cache-data）
 */
function flattenNodes(nodes, result = []) {
    for (const node of nodes) {
        if (node.isDevice) {
            result.push({
                value: node.id,
                label: node.name
            })
        }
        if (node.children) {
            flattenNodes(node.children, result)
        }
    }
    return result
}

// ==================== 数据加载 ====================

async function loadDevices() {
    const res = await getDeviceTree()
    console.log('🚀 ~ loadDevices ~ res:', res)
    const rawTreeData = res
    deviceTreeData.value = buildDeviceTree(rawTreeData)
    cacheData.value = flattenNodes(deviceTreeData.value)
    console.log('🚀 ~ cacheData:', cacheData.value)
}

// 树形勾选事件：只保留设备节点的id，过滤掉父节点（单元/楼/单位/小区）
function handleTreeCheck(data, checkedInfo) {
    // 获取所有选中的节点
    const checkedNodes = treeSelectRef.value?.getCheckedNodes(false, false) || []

    const deviceIds = []
    for (const node of checkedNodes) {
        if (node.isDevice) {
            // 直接勾选设备 → 保留
            deviceIds.push(node.id)
        } else if (node.children) {
            // 勾选父节点（单元/楼等）→ 收集其下所有设备id，父节点id不保留
            collectDeviceIdsFromNode(node.children, deviceIds)
        }
    }

    // 去重并赋值（只包含设备id，不包含父节点id）
    treeSelectValue.value = [...new Set(deviceIds)]
}

/**
 * 从节点下递归收集所有设备id
 */
function collectDeviceIdsFromNode(nodes, targetIds) {
    for (const node of nodes) {
        if (node.isDevice) {
            targetIds.push(node.id)
        } else if (node.children) {
            collectDeviceIdsFromNode(node.children, targetIds)
        }
    }
}

// 设置选中状态（编辑回显）
function setTreeSelectValue(deviceIds) {
    nextTick(() => {
        if (!deviceIds || deviceIds.length === 0) {
            treeSelectValue.value = []
            form.thermostat_list = []
            return
        }

        const deviceIdSet = new Set()

        function findDeviceIds(nodes) {
            for (const node of nodes) {
                if (node.isDevice) {
                    deviceIdSet.add(node.id)
                }
                if (node.children) {
                    findDeviceIds(node.children)
                }
            }
        }

        findDeviceIds(deviceTreeData.value)

        const validIds = deviceIds.filter((id) => deviceIdSet.has(id))
        treeSelectValue.value = validIds
        form.thermostat_list = [...validIds]
    })
}

const page_params = reactive({
    page: 1,
    page_size: 10
})

const total = ref(0)

async function loadGroups() {
    const res = await scheduleApi.list({ ...page_params, thermostat_id: deviceId.value })
    groupList.value = res.lists.map((g) => ({
        ...g,
        showDeviceAll: false
    }))
    total.value = res.total
}

const handleSizeChange = (currentSize) => {
    page_params.page_size = currentSize
    page_params.page = 1
    loadGroups()
}

const handleCurrentChange = (currentPage) => {
    page_params.page = currentPage
    loadGroups()
}

// 新增
function handleAddGroup() {
    editingGroup.value = null
    form.name = ''
    form.week_time = []
    form.switch_time = [{ start_time: '', end_time: '' }]
    form.thermostat_list = []
    treeSelectValue.value = []
    formVisible.value = true
}

// 编辑
async function handleEditGroup(group) {
    const data = await scheduleApi.get(group.id)
    editingGroup.value = data
    form.name = data.name
    form.week_time = data.week_time || []
    form.switch_time = data.switch_time || []

    let deviceIds = []
    if (data.thermostat_list && Array.isArray(data.thermostat_list)) {
        if (data.thermostat_list.length > 0 && typeof data.thermostat_list[0] === 'object') {
            deviceIds = data.thermostat_list.map((d) => d.id)
        } else {
            deviceIds = data.thermostat_list
        }
    }

    formVisible.value = true
    setTreeSelectValue(deviceIds)
}

// 时间段
function addTimeRow() {
    form.switch_time.push({ start_time: '', end_time: '' })
}
function delTimeRow(i) {
    if (form.switch_time.length <= 1) return ElMessage.warning('至少保留一组')
    form.switch_time.splice(i, 1)
}

// 提交
async function submitGroupForm() {
    if (!form.name) return ElMessage.warning('请输入组别名称')
    if (!form.thermostat_list || form.thermostat_list.length === 0) {
        return ElMessage.warning('请至少选择一个设备')
    }

    try {
        const params = { ...form }
        if (editingGroup.value) {
            params.id = editingGroup.value.id
            await scheduleApi.update(params)
        } else {
            await scheduleApi.add(params)
        }
        ElMessage.success('保存成功')
        formVisible.value = false
        loadGroups()
        emit('refresh')
    } catch (e) {
        ElMessage.error('保存失败')
    }
}

// 删除
async function handleDeleteGroup(id) {
    try {
        await ElMessageBox.confirm('确定删除？')
        await scheduleApi.delete(id)
        ElMessage.success('删除成功')
        loadGroups()
        emit('refresh')
    } catch {}
}

// 星期文本
function weekText(week_time = []) {
    return (
        weekList
            .filter((w) => week_time.includes(w.value))
            .map((w) => w.label)
            .join('、') || '未设置'
    )
}
</script>

<style lang="scss" scoped>
.schedule-container {
    overflow-y: auto;
    padding-right: 6px;
}

.group-item {
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    padding: 14px 16px;
    margin-bottom: 12px;
    background: #fdfdfd;
}

.group-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;

    .title {
        font-weight: 600;
        font-size: 15px;
        color: #333;
    }
}

.group-detail {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.detail-item {
    display: flex;
    align-items: flex-start;
    line-height: 24px;

    label {
        min-width: 90px;
        font-weight: 500;
        color: #666;
    }

    .text {
        color: #333;
    }
}

.time-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.time-tag {
    background: #e5f2ff;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 13px;
    color: #1989fa;
}

.text-right {
    text-align: right;
}

.tree-node {
    display: flex;
    align-items: center;
    gap: 6px;

    .device-icon {
        color: #409eff;
        font-size: 14px;
    }

    .folder-icon {
        color: #e6a23c;
        font-size: 14px;
    }

    .device-name {
        color: #606266;
        font-size: 13px;
    }

    .imei-tag {
        color: #909399;
        font-size: 11px;
        background: #f4f4f5;
        padding: 1px 6px;
        border-radius: 3px;
        margin-left: 4px;
    }

    .count-tag {
        color: #909399;
        font-size: 12px;
        margin-left: 4px;
    }
}

.selected-info {
    margin-top: 8px;
    padding: 6px 10px;
    background: #f0f9ff;
    border-radius: 4px;
    font-size: 13px;
    color: #409eff;
}
</style>

<style>
.device-tree-dropdown .el-select-dropdown__item {
    padding: 0;
}
.device-tree-dropdown .el-tree-node__content {
    height: 32px;
}

/* TreeSelect 标签样式：平铺显示所有设备名称 */
.el-tree-select__tags {
    flex-wrap: wrap !important;
    max-height: none !important;
}
.el-tree-select__tags .el-tag {
    margin: 2px 4px 2px 0 !important;
}
</style>
